import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  UserIcon,
  PlusIcon,
  SearchIcon,
  EditIcon,
  TrashIcon,
  BarChart3Icon,
  UsersIcon,
  FileTextIcon,
  MessageSquareIcon,
  Loader2Icon,
  ArrowLeftIcon,
  SettingsIcon
} from "lucide-react";
import { usePermissions } from "../../PermissionsProvider";
import { Agent } from "../../model_gen/agent_base";
import { Application } from "../../model_gen/application";
import { AgentService } from "../../services/AgentService";
import { ApplicationService } from "../../services/ApplicationService";
import AgentCreateModal from "./AgentCreateModal";
import AgentEditModal from "../Agent/AgentEditModal";
import AgentStatsModal from "../Agent/AgentStatsModal";
import moment from "moment";

interface AgentStatistics {
  intent_count: number;
  conversation_count: number;
  total_requests: number;
  avg_response_time: number;
  success_rate: number;
}

const AgentOverview: React.FC = () => {
  const { apiKey } = usePermissions();
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  
  const [application, setApplication] = useState<Application | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<Agent | null>(null);
  const [statsModalOpen, setStatsModalOpen] = useState<Agent | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<Agent | null>(null);
  const [agentStats, setAgentStats] = useState<Record<number, AgentStatistics>>({});

  useEffect(() => {
    if (applicationId && apiKey) {
      loadApplication();
      loadAgents();
    }
  }, [applicationId, apiKey]);

  const loadApplication = () => {
    if (!applicationId || !apiKey) return;
    
    ApplicationService.getApplication(parseInt(applicationId), () => {}, apiKey).subscribe(
      (app: Application) => {
        setApplication(app);
      },
      (error: any) => {
        console.error("Error loading application:", error);
      }
    );
  };

  const loadAgents = () => {
    if (!applicationId || !apiKey) return;
    
    setIsLoading(true);
    AgentService.getAgentsByApplication(parseInt(applicationId), setIsLoading, apiKey).subscribe(
      (response: any) => {
        // Handle both direct array and wrapped response formats
        const agentList = Array.isArray(response) ? response : (response.agents || []);
        setAgents(agentList);
        loadAgentStatistics(agentList);
      },
      (error: any) => {
        console.error("Error loading agents:", error);
        setIsLoading(false);
      }
    );
  };

  const loadAgentStatistics = async (agentList: Agent[]) => {
    const statsPromises = agentList.map(agent =>
      new Promise<{ agentId: number; stats: AgentStatistics }>((resolve) => {
        AgentService.getAgentStatistics(agent.id, () => {}, apiKey).subscribe(
          (stats: AgentStatistics) => {
            resolve({ agentId: agent.id, stats });
          },
          (error: any) => {
            console.error(`Error loading stats for agent ${agent.id}:`, error);
            resolve({
              agentId: agent.id,
              stats: {
                intent_count: 0,
                conversation_count: 0,
                total_requests: 0,
                avg_response_time: 0,
                success_rate: 0
              }
            });
          }
        );
      })
    );

    try {
      const results = await Promise.all(statsPromises);
      const statsMap: Record<number, AgentStatistics> = {};
      results.forEach(({ agentId, stats }) => {
        statsMap[agentId] = stats;
      });
      setAgentStats(statsMap);
    } catch (error) {
      console.error("Error loading agent statistics:", error);
    }
  };

  const handleCreateAgent = () => {
    setCreateModalOpen(true);
  };

  const handleEditAgent = (agent: Agent) => {
    setEditModalOpen(agent);
  };

  const handleViewStats = (agent: Agent) => {
    setStatsModalOpen(agent);
  };

  const handleDeleteAgent = (agent: Agent) => {
    if (!apiKey) return;
    
    AgentService.deleteAgent(agent.id, () => {}, apiKey).subscribe(
      () => {
        setDeleteDialogOpen(null);
        loadAgents(); // Refresh the list
      },
      (error: any) => {
        console.error("Error deleting agent:", error);
      }
    );
  };

  const handleAgentCreated = () => {
    setCreateModalOpen(false);
    loadAgents();
  };

  const handleAgentUpdated = () => {
    setEditModalOpen(null);
    loadAgents();
  };

  const filteredAgents = agents.filter(agent =>
    agent.agent_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (agent.description && agent.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (!applicationId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-lg text-muted-foreground">No application selected</p>
          <Button onClick={() => navigate("/applications")}>
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Applications
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/application/${applicationId}`)}
            >
              <ArrowLeftIcon className="w-4 h-4" />
            </Button>
            <h1 className="text-3xl font-gc-demi-bold text-foreground">Agents</h1>
          </div>
          <p className="text-muted-foreground">
            {application?.application_name && (
              <span>Manage agents for <span className="font-gc-med">{application.application_name}</span></span>
            )}
          </p>
        </div>
        <Button onClick={handleCreateAgent}>
          <PlusIcon className="w-4 h-4 mr-2" />
          Create Agent
        </Button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative max-w-sm">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search agents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2Icon className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Agents Grid */}
      {!isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAgents.map((agent) => {
            const stats = agentStats[agent.id];
            return (
              <Card key={agent.id} className="border-border hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-maitai-green/10 rounded-lg">
                        <UserIcon className="w-5 h-5 text-maitai-green" />
                      </div>
                      <div className="space-y-1">
                        <CardTitle className="text-lg font-gc-med">{agent.agent_name}</CardTitle>
                        {agent.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2">{agent.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewStats(agent)}
                      >
                        <BarChart3Icon className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditAgent(agent)}
                      >
                        <EditIcon className="w-4 h-4" />
                      </Button>
                      <AlertDialog
                        open={deleteDialogOpen?.id === agent.id}
                        onOpenChange={(open) => setDeleteDialogOpen(open ? agent : null)}
                      >
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <TrashIcon className="w-4 h-4 text-destructive" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Agent</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete the agent "{agent.agent_name}"? 
                              This will remove all intent associations but won't delete the intents themselves.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteAgent(agent)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Statistics */}
                  {stats && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1">
                          <FileTextIcon className="w-3 h-3 text-muted-foreground" />
                          <p className="text-xs text-muted-foreground">Intents</p>
                        </div>
                        <p className="text-sm font-gc-med">{stats.intent_count}</p>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-1">
                          <MessageSquareIcon className="w-3 h-3 text-muted-foreground" />
                          <p className="text-xs text-muted-foreground">Conversations</p>
                        </div>
                        <p className="text-sm font-gc-med">{stats.conversation_count}</p>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/application/${applicationId}/agent/${agent.id}/intents`)}
                      className="flex-1"
                    >
                      <SettingsIcon className="w-3 h-3 mr-2" />
                      Manage Intents
                    </Button>
                  </div>

                  {/* Created Date */}
                  <div className="pt-2 border-t border-border">
                    <p className="text-xs text-muted-foreground">
                      Created {moment(agent.date_created).format("MMM D, YYYY")}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && filteredAgents.length === 0 && (
        <div className="text-center py-12">
          <div className="mx-auto w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-4">
            <UsersIcon className="w-12 h-12 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-gc-med text-foreground mb-2">
            {searchTerm ? "No agents found" : "No agents yet"}
          </h3>
          <p className="text-muted-foreground mb-6">
            {searchTerm
              ? "Try adjusting your search terms"
              : "Create your first agent to organize and manage your intents"
            }
          </p>
          {!searchTerm && (
            <Button onClick={handleCreateAgent}>
              <PlusIcon className="w-4 h-4 mr-2" />
              Create Agent
            </Button>
          )}
        </div>
      )}

      {/* Modals */}
      <AgentCreateModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onAgentCreated={handleAgentCreated}
        applicationId={applicationId ? parseInt(applicationId) : undefined}
      />

      {editModalOpen && (
        <AgentEditModal
          open={!!editModalOpen}
          onClose={() => setEditModalOpen(null)}
          onAgentUpdated={handleAgentUpdated}
          agent={editModalOpen}
        />
      )}

      {statsModalOpen && (
        <AgentStatsModal
          open={!!statsModalOpen}
          onClose={() => setStatsModalOpen(null)}
          agent={statsModalOpen}
          statistics={agentStats[statsModalOpen.id]}
        />
      )}
    </div>
  );
};

export default AgentOverview; 